import { canAutoRecover, getProxySettings, pauseProxyExecution, setProxyRecoveryStatus,
  subscribeProxySettings, updateProxySettings } from './proxySettings.js'
import { queueRecoveredSchedules } from './scheduledRecovery.js'

export const RECOVERY_DELAYS = [5000, 15000, 30000, 60000]

// 应用级唯一恢复器；手动恢复、联网事件和退避定时器共用同一个入口。
export class ProxyRecovery {
  constructor({ getRuntime, settings = getProxySettings, subscribe = subscribeProxySettings,
    update = updateProxySettings, pause = pauseProxyExecution, status = setProxyRecoveryStatus,
    setTimer = (callback, delay) => setTimeout(callback, delay), clearTimer = handle => clearTimeout(handle),
    now = Date.now, events = globalThis.window } = {}) {
    Object.assign(this, { getRuntime, settings, update, pause, status, setTimer, clearTimer, now, events })
    this.attempt = 0
    this.version = 0
    this.onOnline = () => { if (canAutoRecover(this.settings())) void this.resume().catch(() => {}) }
    this.onMigration = () => this.dispose()
    this.unsubscribe = subscribe(() => this.sync())
    this.getRuntime().taskManager?.addEventListener?.('migrationPaused', this.onMigration)
    events?.addEventListener('online', this.onOnline)
    this.sync()
  }

  sync() {
    const state = this.settings()
    const identity = JSON.stringify([state.paused, state.code, state.pausedAt, state.accessKey])
    if (identity === this.identity) return
    this.identity = identity
    this.version++
    this.controller?.abort()
    this.clearTimer(this.timer)
    this.timer = null
    this.attempt = 0
    if (['USER_STOP', 'KEY_CLEARED'].includes(state.code)) this.getRuntime().taskManager?.discardTransportCheckpoint()
    if (!state.paused && this.getRuntime().taskManager?.transportPaused && !this.inFlight) {
      queueMicrotask(() => { if (!this.disposed) void this.resume().catch(() => {}) })
    }
    if (canAutoRecover(state) && !this.getRuntime().taskManager?.migrationPaused) this.schedule()
  }

  schedule() {
    if (this.disposed || this.timer || !canAutoRecover(this.settings()) || this.getRuntime().taskManager?.migrationPaused) return
    const delay = RECOVERY_DELAYS[Math.min(this.attempt++, RECOVERY_DELAYS.length - 1)]
    this.status({ recovering: false, nextRetryAt: this.now() + delay })
    this.timer = this.setTimer(() => { this.timer = null; void this.resume().catch(() => {}) }, delay)
  }

  resume() {
    if (this.inFlight) return this.inFlight
    const version = this.version
    this.clearTimer(this.timer)
    this.timer = null
    const controller = new AbortController()
    this.controller = controller
    this.status({ recovering: true, nextRetryAt: 0 })
    this.inFlight = this.tryResume(version, controller.signal).finally(() => {
      this.inFlight = null
      if (!this.disposed) {
        this.status({ recovering: false })
        this.schedule()
      }
    })
    return this.inFlight
  }

  async tryResume(version, signal) {
    const { taskManager, apiService, accounts, addLog, prepareScheduledJobsForResume } = this.getRuntime()
    const catchUp = canAutoRecover(this.settings())
    const current = () => !this.disposed && version === this.version && !signal.aborted && !taskManager?.migrationPaused
    if (!taskManager || !apiService || !current()) throw new Error('请先等待应用初始化或刷新页面')
    let committed = false
    try {
      await apiService.transport.verifyAccessKey({ signal, probe: true })
      const targets = new Map()
      for (const account of Object.values(accounts || {})) {
        if (account.server && !targets.has(account.server)) targets.set(account.server, account)
      }
      if (!targets.size) {
        const error = new Error('没有可用于验证游戏连通性的账号，请先添加账号')
        error.code = 'NO_ACCOUNTS'
        throw error
      }
      for (const account of targets.values()) {
        if (!current()) return false
        await apiService.probeConnection(account, signal)
      }
      await taskManager.waitForTransportIdle()
      if (!current()) return false
      let dueJobs = []
      let resetDailyPlans = false
      try {
        const prepared = prepareScheduledJobsForResume?.({ catchUp })
        dueJobs = prepared?.dueJobs || []
        resetDailyPlans = prepared?.resetDailyPlans || false
        this.update({ paused: false, reason: '', code: '', pausedAt: 0, nextRetryAt: 0, recovering: false })
        committed = true
      } catch {
        const error = new Error('恢复未完成，无法保存浏览器本地状态')
        error.code = 'STORAGE_ERROR'
        throw error
      }
      taskManager.resumeAfterTransport()
      if (resetDailyPlans) taskManager.rescheduleDailyTaskPlans()
      queueRecoveredSchedules(taskManager, dueJobs, this.now())
      this.status({ lastRecoveredAt: this.now() })
      addLog?.('游戏连接已恢复，重新执行中断任务并继续剩余队列与定时调度', 'info')
      taskManager.processQueue()
      return true
    } catch (error) {
      if (!current() && !committed) return false
      if (committed) {
        this.pause('任务恢复失败，请检查日志后手动恢复', 'RESUME_ERROR')
        throw error
      }
      if (error.code && !canAutoRecover({ paused: true, code: error.code })) this.pause(error.message, error.code)
      else addLog?.('连接尚未恢复，将继续自动重试', 'warning')
      throw error
    }
  }

  dispose() {
    this.disposed = true
    this.version++
    this.clearTimer(this.timer)
    this.controller?.abort()
    this.unsubscribe?.()
    this.events?.removeEventListener('online', this.onOnline)
    this.getRuntime().taskManager?.removeEventListener?.('migrationPaused', this.onMigration)
  }
}
